/**
 * Talks to the generate worker: `generateLayouts(options, species, themes)` resolves with one result per option.
 * One worker is kept for the wizard's lifetime; a newer request makes older answers be ignored by the caller.
 * Falls back to running on the page if the worker can't start.
 */
import type { GenerateOptions, PlanDoc, Species, SpeciesPick, Theme } from '../../../shared/types';
import type { GenerateRequest, GenerateResponse } from '../../workers/generate.worker';

/** One generated layout. */
export interface Layout {
  doc: PlanDoc;
  unplaced: SpeciesPick[];
}

let worker: Worker | null = null;
let nextId = 1;
const waiting = new Map<number, { resolve: (r: Layout[]) => void; reject: (e: Error) => void }>();

function getWorker(): Worker | null {
  if (worker) return worker;
  try {
    worker = new Worker(new URL('../../workers/generate.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return null;
  }
  worker.onmessage = (e: MessageEvent<GenerateResponse>) => {
    const w = waiting.get(e.data.id);
    if (!w) return;
    waiting.delete(e.data.id);
    if (e.data.ok) w.resolve(e.data.results);
    else w.reject(new Error(e.data.message));
  };
  worker.onerror = (e) => {
    e.preventDefault();
    for (const w of waiting.values()) w.reject(new Error(e.message || 'The layout maker stopped.'));
    waiting.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

/** Makes the layouts in the worker (or on the page if there's no worker). */
export async function generateLayouts(options: GenerateOptions[], species: Species[], themes: Theme[]): Promise<Layout[]> {
  const req: GenerateRequest = { id: nextId++, options, species, themes };
  const w = getWorker();
  if (!w) {
    const { runGenerate } = await import('../../workers/generate.worker');
    const res = runGenerate(req);
    if (!res.ok) throw new Error(res.message);
    return res.results;
  }
  return new Promise((resolve, reject) => {
    waiting.set(req.id, { resolve, reject });
    w.postMessage(req);
  });
}

/** Stops the worker (when the wizard closes). */
export function stopGenerating(): void {
  for (const w of waiting.values()) w.reject(new Error('stopped'));
  waiting.clear();
  worker?.terminate();
  worker = null;
}
